import { InputAdornment, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import "./styles.css";
import { sxObservationTextField } from "./sxProps";
import { useAccessibility } from "../../../../Store/contexts/AccessibilityContext";
import ModalFeedBack from "../../../../components/ModalFeedBack";
import TypographyAccessibility from "../../../../components/Accessibility/TypographyAccessibility";
import DefaultButton from "../../../../components/Accessibility/DefaultButton";
import { sxFormError } from "../../../../assets/styles/CommonSxProps";
import PageTitle from "../../../../components/Accessibility/PageTitle";
import VoltarButton from "../../../../components/Accessibility/VoltarButton";
import LoadingApp from "../../../../components/LoadingApp";
import { sxDarkGreyBody } from "../../../MeuPlano/PermissoesVisualizacao/sxProps";
import { sxLabel } from "../../CadastroBoleto/sxProps";
import AnexoButton from "../../../../components/AnexoButton";
import { Anexo } from "../../../../interfaces/entities/OutrasSolictacoesFinanceiras/Anexo";
import { NovaSolicitacaoValidation } from "../../../../interfaces/validation/Contrato/NovaSolicitacaoValidation";
import { INovaSolicitacao } from "../../../../interfaces/entities/OutrasSolictacoesFinanceiras/NovaSolicitacao";
import { useNavigate } from "react-router-dom";
import {
  uploadArquivo,
  enviarSolicitacao,
} from "../../../../services/Financeiro/OutrasSolicitacoes";

function extrairProtocolo(resposta: string): string | null {
  const match = resposta.match(/Protocolo:\s*(\d+)/i);
  return match ? match[1] : null;
}

const NovaSolicitacao: React.FC = () => {
  const navigate = useNavigate();
  const [fillingForm, setFillingForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [anexos, setAnexos] = useState<Array<Anexo>>([]);
  const [protocolo, setProtocolo] = useState<string | null>(null);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: "error" | "success" | "warning";
    title?: string;
    textButton?: string;
    subTitle?: any;
    closeAction: () => void;
    showButtonModal: boolean;
    buttonAction?: () => void;
  }>(null);

  const openModal = (
    icon: "success" | "warning" | "error",
    title: string,
    subTitle: any,
    textButton: string,
    closeAction: () => void,
    buttonAction: () => void
  ) => {
    setModalFeedback({
      icon,
      title,
      subTitle,
      textButton,
      showButtonModal: true,
      closeAction: closeAction,
      buttonAction: buttonAction,
    });
  };

  const formik = useFormik({
    initialValues: {
      descricao: "",
    },
    validationSchema: NovaSolicitacaoValidation,
    onSubmit: (values) => {
      sendSolicitation({
        descricao: values.descricao,
        file: anexos[0],
      });
    },
    validateOnChange: false,
  });

  const resetAndLoadForm = async () => {
    formik.resetForm();
    setAnexos([]);
  };

  const goToStart = () => {
    toggleFillingForm();
    closeModal();
  };

  const goToMinhasSolicitacoes = () => {
    navigate("/financeiro/outrasSolicitacoesFinanceiras/minhasSolicitacoes", {
      state: { 
        reload: true,
        timestamp: Date.now()
      },
    });
    closeModal();
  };

  const goToListagem = () => {
    navigate("/financeiro/outrasSolicitacoesFinanceiras");
    closeModal();
  };

  const sendSolicitation = async (novaSolicitacao: INovaSolicitacao) => {
    setLoading(true);
    try {
      // 1. Upload dos anexos e coleta dos GUIDs
      const guids: string[] = [];
      if (anexos.length > 0) {
        for (const anexo of anexos) {
          const extensao =
            anexo.nomeArquivo.split(".").pop()?.toLowerCase() || "";
          const guid = await uploadArquivo(extensao, anexo.arquivo);
          if (!guid) {
            openModal(
              "error",
              "Erro",
              `Falha ao fazer upload do arquivo: ${anexo.nomeArquivo}`,
              "Fechar",
              closeModal,
              closeModal
            );
            setLoading(false);
            return;
          }
          guids.push(guid);
        }
      }
      // 2. Envio da solicitação
      const response = await enviarSolicitacao(
        novaSolicitacao.descricao,
        guids
      );
      if (response) {
        const protocoloExtraido = extrairProtocolo(response);
        setProtocolo(protocoloExtraido);

        openModal(
          "success",
          "Solicitação realizada com sucesso!",
          (
            <div>
            Seu protocolo é: <strong>{protocoloExtraido}</strong>
            <div className="modal-subtitle-spacing">
              Sua solicitação foi realizada e você pode acompanhá-la em 'Minhas
              solicitações'.
            </div>
          </div>
          ),
          "Ir para minhas solicitaçõs",
          goToListagem,
          goToMinhasSolicitacoes
        );
        formik.resetForm();
        setAnexos([]);
      } else {
        openModal(
          "error",
          "Erro",
          "Ocorreu um erro ao enviar a solicitação.",
          "Fechar",
          closeModal,
          closeModal
        );
      }
    } catch (error) {
      console.error(error);
      openModal(
        "error",
        "Erro",
        "Ocorreu um erro inesperado ao enviar a solicitação.",
        "Fechar",
        closeModal,
        closeModal
      );
    } finally {
      setLoading(false);
    }
  };

  const allFieldsFilled =
    Object.values(formik.values).every((value) => value !== "");

  const closeModal = () => {
    setModalFeedback(null);
  };

  const toggleFillingForm = () => {
    navigate(-1);
  };

  return (
    <>
      <LoadingApp showLoading={loading} />
      <form
        className="feature-container"
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <PageTitle text="Nova solicitação" />

        <div className="input-holder width-100">
          <TypographyAccessibility sx={sxLabel}>
            Descrição
          </TypographyAccessibility>
          <TextField
            id="outlined-textarea"
            type="text"
            value={formik.values.descricao}
            onChange={(e) => {
              formik.setFieldValue("descricao", e.target.value);
            }}
            placeholder="Descreva sua solicitação"
            multiline
            inputProps={{ maxLength: 500 }}
            sx={{
              ...sxObservationTextField,
              "& .MuiInputAdornment-root": {
                position: "absolute",
                bottom: 12,
                right: 8,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" color="textSecondary">
                    {formik.values.descricao.length} / 500
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
          <TypographyAccessibility sx={sxFormError}>
            {formik.errors.descricao}
          </TypographyAccessibility>
        </div>

        <div className="anexo-negativa-div">
          <TypographyAccessibility sx={sxDarkGreyBody} maxWidth={"650px"}>
            Anexe exames ou documentos na solicitação:
          </TypographyAccessibility>
          <div className="anexo-negativa-file-button">
            <AnexoButton
              anexos={anexos}
              salvarAnexos={(anexos) => {
                setAnexos(anexos);
              }}
              maxQtd={1}
            />
          </div>
          <TypographyAccessibility sx={sxDarkGreyBody} maxWidth={"650px"}>
            Você pode anexar arquivos .pdf .jpg .doc e .png de até 4MB, sendo de
            notas fiscais/recibos, pedidos médicos, relatórios médicos, entre
            outros.
          </TypographyAccessibility>
        </div>
        <DefaultButton
          text="Solicitar"
          type="submit"
          disabled={!allFieldsFilled}
        />
        <VoltarButton onClick={toggleFillingForm} />
      </form>

      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle}
        icon={modalFeedback?.icon as "success" | "error" | "warning"}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
    </>
  );
};

export default NovaSolicitacao;
